import {
  TournamentCompetition,
  TournamentEntry,
  type TournamentMatch,
  TournamentPlayer,
} from "@mixitone/models";
import MixitoneController from "controllers/MixitoneController";
import { List, setupModelRelations } from "@mixitone/oom";
import { isPresent, withStateToggle } from "@mixitone/util";
import routeService from "services/RouteService";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import { getMatchEntryIds } from "tournament-state/tournamentMatchEligibility";
import {
  canRecordEntryMatchResult,
  entryMatchWinnerSelectedPayload,
  type EntryMatchResult,
} from "./entryMatchResult";

export type EntryMatchRow = {
  match: TournamentMatch;
  opponent: TournamentEntry | null;
  result: EntryMatchResult;
  scoreLabel: string | null;
};

interface State {
  loading: boolean;
  matches: EntryMatchRow[];
  recordingMatchId?: string;
}

interface Props {
  competitionId: string;
  entryId: string;
}

class EntryMatchesController extends MixitoneController<State, Props> {
  private competitionId = "";
  private entryId = "";

  override get initialState(): State {
    return {
      loading: true,
      matches: [],
    };
  }

  override async initialize(props: Props) {
    await this.changeProps(props);
  }

  override async changeProps(props: Props) {
    this.competitionId = props.competitionId;
    this.entryId = props.entryId;
    if (!props.competitionId || !props.entryId) {
      this.state.matches = [];
      this.state.loading = false;
      return;
    }
    await this.loadMatches(props.competitionId, props.entryId);
  }

  @withStateToggle("loading")
  private async loadMatches(competitionId: string, entryId: string) {
    await this.reloadMatches(competitionId, entryId);
  }

  private async reloadMatches(competitionId: string, entryId: string) {
    const competition = await TournamentCompetition.query().find(competitionId);
    if (!competition) {
      this.state.matches = [];
      return;
    }

    const projected = await new TournamentDataAdapter().loadProjectedMatches(competition);
    const entryMatches = projected
      .filter((match) => getMatchEntryIds(match).includes(entryId))
      .sort((a, b) => {
        const roundDiff = (a.round ?? 0) - (b.round ?? 0);
        if (roundDiff !== 0) return roundDiff;
        return (a.match_index ?? 0) - (b.match_index ?? 0);
      });

    const opponentIds = [
      ...new Set(entryMatches.flatMap((match) => getMatchEntryIds(match)).filter((id) => id !== entryId)),
    ];

    const entries =
      opponentIds.length > 0
        ? await TournamentEntry.query().in("id", opponentIds).preload("registrations", "competition").all()
        : [];
    const registrations = entries.flatMap((entry) => entry.registrations);
    const playerIds = registrations.map((r) => r.tournament_player_id).filter(isPresent);
    const players = playerIds.length > 0 ? await TournamentPlayer.query().in("id", playerIds).all() : [];
    setupModelRelations({ entries, registrations, players });

    const entriesById = new List(...entries);

    this.state.matches = entryMatches.map((match) => {
      const opponentId = getMatchEntryIds(match).find((id) => id !== entryId);
      const opponent = opponentId ? (entriesById.findById(opponentId) ?? null) : null;
      return {
        match,
        opponent,
        result: this.matchResult(match, entryId),
        scoreLabel: this.scoreLabel(match, entryId, opponentId),
      };
    });
  }

  async actionRecordResult(row: EntryMatchRow, result: "won" | "lost") {
    if (!canRecordEntryMatchResult(row)) return;
    if (!this.competitionId || !this.entryId) return;
    const payload = entryMatchWinnerSelectedPayload({
      entryId: this.entryId,
      match: row.match,
      result,
    });
    if (!payload) return;

    const competition = await TournamentCompetition.query().find(this.competitionId);
    if (!competition) return;

    this.state.recordingMatchId = row.match.id;
    try {
      await new TournamentDataAdapter().appendEvent({
        competition,
        eventType: "winner_selected",
        payload,
      });
      window.dispatchEvent(
        new CustomEvent("queueGeneration", {
          detail: { competitionId: this.competitionId, immediate: true },
        }),
      );
      await this.reloadMatches(this.competitionId, this.entryId);
    } finally {
      this.state.recordingMatchId = undefined;
    }
  }

  private matchResult(match: TournamentMatch, entryId: string): EntryMatchRow["result"] {
    if (match.isVoided || match.status === "voided") return "voided";
    if (match.entries?.winners?.includes(entryId)) return "won";
    if (match.entries?.losers?.includes(entryId)) return "lost";
    return null;
  }

  private scoreLabel(match: TournamentMatch, entryId: string, opponentId: string | undefined): string | null {
    const scores = match.entries?.score;
    if (!scores) return null;
    const own = scores[entryId];
    const theirs = opponentId ? scores[opponentId] : undefined;
    if (own != null && own !== "" && theirs != null && theirs !== "") {
      return `${own}–${theirs}`;
    }
    if (own != null && own !== "") return String(own);
    if (theirs != null && theirs !== "") return String(theirs);
    return null;
  }

  actionOpenMatch(match: TournamentMatch) {
    if (!match.tournament_competition_id || !match.id) return;
    routeService.openModal("/tournament/:id/matches/:matchId/edit", {
      params: { id: match.tournament_competition_id, matchId: match.id },
      state: {
        attributes: {
          tournamentId: match.tournament_competition_id,
          tournament_competition_id: match.tournament_competition_id,
        },
        matchList: this.state.matches.map((row) => row.match.id!).filter(Boolean),
      },
    });
  }
}

export default EntryMatchesController;
